import type { SlotProduct } from '@/api/resources/machines'
import type { SelectableProduct } from '@/api/resources/slots'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { ApiError } from '@/api/client'
import { machineQueryOptions } from '@/api/resources/machines'
import { createProductRequest } from '@/api/resources/product-requests'
import {
  deleteNextProduct,
  selectableProductsQueryOptions,
  updateSlotProduct,
} from '@/api/resources/slots'
import { ProductRequestFormDialog } from '@/components/machines/planogram/product-request-form-dialog'
import { ReplaceProductSheet } from '@/components/machines/planogram/replace-product-sheet'
import { useRouteModal } from '@/hooks/use-route-modal'
import { useServerErrors } from '@/hooks/use-server-errors'
import { isCustomizeMachine, isProfessionalMachine } from '@/lib/machines'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute('/_app/machines/$machineCode/slots_/$slotId/replace')({
  head: () => ({ meta: [{ title: 'Replace Slot - MyJunea' }] }),
  component: ReplaceProductRoute,
})

function ReplaceProductRoute(): React.ReactElement {
  const { machineCode, slotId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { open, handleClose, handleOpenChange } = useRouteModal(
    () =>
      void navigate({ to: '/machines/$machineCode', params: { machineCode }, resetScroll: false }),
  )

  const slotIdNum = Number(slotId)

  const { data: machine } = useQuery(machineQueryOptions(machineCode))

  const currentSlot = machine?.shelves.flatMap((s) => s.slots).find((s) => s.id === slotIdNum)

  const currentProduct: SlotProduct | null = currentSlot?.product ?? null
  const nextProduct: SlotProduct | null = currentSlot?.next_product ?? null
  const slotPosition = currentSlot?.position ?? 0
  const machineName = machine?.name ?? machineCode

  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, { wait: 300 })
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    () => nextProduct?.id ?? null,
  )
  const [page, setPage] = useState(1)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [confirmedProduct, setConfirmedProduct] = useState<SelectableProduct | null>(null)
  const [requestDialogOpen, setRequestDialogOpen] = useState(false)

  const canRequestProduct = machine != null && isCustomizeMachine(machine)
  const showRefillPrice = machine != null && isProfessionalMachine(machine)
  const machineType = machine?.type ?? ''

  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const { data: productsData, isLoading } = useQuery(
    selectableProductsQueryOptions(machineCode, slotIdNum, {
      search: debouncedSearch || undefined,
      page,
    }),
  )

  const products = productsData?.data ?? []
  const currentPageMeta = productsData?.meta.current_page ?? 1
  const lastPage = productsData?.meta.last_page ?? 1

  const replaceMutation = useMutation({
    mutationFn: async () =>
      updateSlotProduct(machineCode, slotIdNum, {
        product_id: selectedProductId!,
        as_next_product: true,
        stock: 0,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.detail(machineCode) })
      toast.success('Product replacement scheduled')
      const selected = products.find((p) => p.id === selectedProductId) ?? null
      setConfirmedProduct(selected)
      setIsConfirmed(true)
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to replace product'
      toast.error(message)
    },
  })

  const cancelMutation = useMutation({
    mutationFn: async () => deleteNextProduct(machineCode, slotIdNum),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.detail(machineCode) })
      toast.success('Replacement cancelled')
      handleClose()
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to cancel replacement'
      toast.error(message)
    },
  })

  const requestMutation = useMutation({
    mutationFn: createProductRequest.bind(null, machineCode),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.productRequests.all })
      toast.success('Your product request has been sent. The Junea team will review it.')
      clearServerErrors()
      setRequestDialogOpen(false)
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        handleMutationError(error)
        if (!(error instanceof ApiError && error.status === 422)) {
          toast.error(error.message)
        }
      }
    },
  })

  const handleSearchChange = (value: string): void => {
    setSearch(value)
    setPage(1)
    setSelectedProductId(null)
  }

  const handlePageChange = (newPage: number): void => {
    setPage(newPage)
    setSelectedProductId(null)
  }

  return (
    <>
      <ReplaceProductSheet
        open={open}
        onOpenChange={handleOpenChange}
        slotPosition={slotPosition}
        currentProduct={currentProduct}
        nextProduct={nextProduct}
        products={products}
        isLoading={isLoading}
        search={search}
        onSearchChange={handleSearchChange}
        selectedProductId={selectedProductId}
        onProductSelect={setSelectedProductId}
        onConfirm={() => replaceMutation.mutate()}
        isSubmitting={replaceMutation.isPending}
        onCancelReplacement={() => cancelMutation.mutate()}
        isCancellingReplacement={cancelMutation.isPending}
        currentPage={currentPageMeta}
        lastPage={lastPage}
        onPageChange={handlePageChange}
        isConfirmed={isConfirmed}
        machineName={machineName}
        operatorName={null}
        currentStock={currentSlot?.stock ?? 0}
        replacementProduct={confirmedProduct}
        isRevenuePricing={machine?.subscription_type === 'revenue'}
        showRefillPrice={showRefillPrice}
        canRequestProduct={canRequestProduct}
        onRequestProduct={() => setRequestDialogOpen(true)}
      />
      <ProductRequestFormDialog
        open={requestDialogOpen}
        onOpenChange={(o) => {
          if (!o) {
            clearServerErrors()
          }
          setRequestDialogOpen(o)
        }}
        machineType={machineType}
        isSubmitting={requestMutation.isPending}
        serverErrors={serverErrors}
        onSubmit={(input) => requestMutation.mutate(input)}
      />
    </>
  )
}
